import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarDays, Car, CheckCircle2, Loader2, LogOut, Phone, Plane, PlaneLanding, UserCheck, Users } from "lucide-react";
import { API, formatGBP } from "@/lib/site";

const inputCls =
  "w-full rounded-xl border border-white/15 bg-[#0b101b] px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-[#38bdf8] focus:outline-none";
const labelCls = "mb-2 block text-[11px] font-mono-lbl uppercase tracking-wider text-slate-400";

const fmtErr = (d) =>
  typeof d === "string" ? d : Array.isArray(d) ? d.map((e) => e?.msg).filter(Boolean).join(" ") : "Something went wrong";

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function Admin() {
  const [user, setUser] = useState(null); // null = checking, false = logged out
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [bookings, setBookings] = useState(null);
  const [day, setDay] = useState(todayISO());
  const [tab, setTab] = useState("paid");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(data);
      } catch {
        try {
          await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
          const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
          setUser(data);
        } catch {
          setUser(false);
        }
      }
    })();
  }, []);

  useEffect(() => {
    if (!user) return;
    axios
      .get(`${API}/admin/bookings`, { withCredentials: true })
      .then((r) => setBookings(r.data.bookings))
      .catch(() => setBookings([]));
  }, [user]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, creds, { withCredentials: true });
      setUser(data);
    } catch (err) {
      setError(fmtErr(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setUser(false);
    setBookings(null);
  };

  const setLeg = async (bookingId, leg, status) => {
    try {
      await axios.patch(`${API}/admin/bookings/${bookingId}/legs/${leg}`, { status }, { withCredentials: true });
      setBookings((prev) =>
        (prev || []).map((b) => (b.id === bookingId ? { ...b, leg_status: { ...(b.leg_status || {}), [leg]: status } } : b))
      );
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Could not update job status");
    }
  };

  const paid = useMemo(() => (bookings || []).filter((b) => b.status === "paid"), [bookings]);
  const revenue = useMemo(() => paid.reduce((s, b) => s + b.amount_gbp, 0), [paid]);

  const dayJobs = useMemo(() => {
    const jobs = [];
    for (const b of paid) {
      if (b.date === day)
        jobs.push({ key: b.id + "-out", time: b.time, leg: "OUT", legKey: "out", jobStatus: b.leg_status?.out || "pending", b,
          route: b.direction === "to_airport" ? `${b.pickup_zone} → ${b.airport_name}` : `${b.airport_name} → ${b.pickup_zone}`,
          flight: b.direction === "from_airport" ? b.flight_number : "" });
      if (b.return_journey && b.return_date === day)
        jobs.push({ key: b.id + "-rtn", time: b.return_time, leg: "RTN", legKey: "rtn", jobStatus: b.leg_status?.rtn || "pending", b,
          route: b.direction === "to_airport" ? `${b.airport_name} → ${b.pickup_zone}` : `${b.pickup_zone} → ${b.airport_name}`,
          flight: b.return_flight_number });
    }
    return jobs.sort((a, c) => a.time.localeCompare(c.time));
  }, [paid, day]);

  const shown = useMemo(
    () => (bookings || []).filter((b) => (tab === "all" ? true : tab === "paid" ? b.status === "paid" : b.status !== "paid")),
    [bookings, tab]
  );

  if (user === null)
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0b101b]" data-testid="admin-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#38bdf8]" />
      </div>
    );

  if (user === false)
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0b101b] px-6" data-testid="admin-login-page">
        <form onSubmit={login} className="w-full max-w-sm rounded-3xl border border-[#38bdf8]/30 bg-gradient-to-b from-[#151e32] to-[#0d1424] p-8 shadow-2xl" data-testid="admin-login-form">
          <div className="mb-6 text-center">
            <div className="font-disp text-2xl uppercase text-white">EL &amp; MJ TRAVEL</div>
            <div className="mt-1 font-mono-lbl text-xs uppercase tracking-widest text-[#38bdf8]">Dispatch Desk Login</div>
          </div>
          <label className={labelCls}>Email</label>
          <input data-testid="admin-email-input" type="email" required value={creds.email}
            onChange={(e) => setCreds((c) => ({ ...c, email: e.target.value }))} placeholder="admin@elmjtravel.co.uk" className={inputCls} />
          <div className="mt-4">
            <label className={labelCls}>Password</label>
            <input data-testid="admin-password-input" type="password" required value={creds.password}
              onChange={(e) => setCreds((c) => ({ ...c, password: e.target.value }))} placeholder="••••••••" className={inputCls} />
          </div>
          {error && <div className="mt-4 rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-2.5 text-xs text-red-300" data-testid="admin-login-error">{error}</div>}
          <button type="submit" data-testid="admin-login-button" disabled={busy}
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-[#38bdf8] px-6 py-3.5 text-sm font-bold uppercase tracking-wider text-[#0b101b] transition-colors hover:bg-white disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
          </button>
        </form>
      </div>
    );

  return (
    <div className="min-h-screen bg-[#0b101b] text-[#f1f5f9]" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#0b101b]/90 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-6">
          <div className="flex items-center gap-3">
            <span className="font-disp text-xl uppercase text-white">EL &amp; MJ TRAVEL</span>
            <span className="rounded-full border border-[#38bdf8]/30 bg-[#38bdf8]/10 px-2.5 py-0.5 font-mono-lbl text-[10px] uppercase tracking-widest text-[#38bdf8]">Dispatch Desk</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden font-mono-lbl text-xs text-slate-400 sm:block">{user.email}</span>
            <button onClick={logout} data-testid="admin-logout-button"
              className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-bold uppercase tracking-wider text-slate-300 transition-colors hover:border-red-400/50 hover:text-red-300">
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-10">
        {/* Stats */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5" data-testid="stat-paid-count">
            <div className="font-mono-lbl text-[10px] uppercase tracking-widest text-slate-400">Paid Bookings</div>
            <div className="mt-1 font-disp text-4xl text-white">{paid.length}</div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5" data-testid="stat-revenue">
            <div className="font-mono-lbl text-[10px] uppercase tracking-widest text-slate-400">Revenue Taken</div>
            <div className="mt-1 font-disp text-4xl text-[#38bdf8]">{formatGBP(revenue)}</div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5" data-testid="stat-day-pickups">
            <div className="font-mono-lbl text-[10px] uppercase tracking-widest text-slate-400">Pickups on {day}</div>
            <div className="mt-1 font-disp text-4xl text-white">{dayJobs.length}</div>
          </div>
        </div>

        {/* Day schedule */}
        <section className="mt-10 rounded-3xl border border-white/10 bg-white/5 p-6" data-testid="day-schedule-section">
          <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
            <div>
              <span className="font-mono-lbl text-xs uppercase tracking-widest text-[#38bdf8]">Daily Pickups</span>
              <h2 className="mt-1 font-disp text-2xl uppercase text-white">Run Sheet</h2>
            </div>
            <div>
              <label className={labelCls}>Show day</label>
              <input type="date" data-testid="schedule-date-input" value={day} onChange={(e) => setDay(e.target.value)} className={inputCls} />
            </div>
          </div>
          {dayJobs.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-500" data-testid="schedule-empty">No pickups scheduled for this day.</p>
          ) : (
            <div className="space-y-3" data-testid="schedule-list">
              {dayJobs.map((j) => (
                <div key={j.key} data-testid={`schedule-item-${j.key}`}
                  className={`flex flex-col gap-3 rounded-2xl border border-white/10 bg-[#0b101b]/70 p-4 sm:flex-row sm:items-center sm:justify-between ${j.jobStatus === "completed" ? "opacity-60" : ""}`}>
                  <div className="flex items-center gap-4">
                    <span className={`flex items-center gap-1.5 rounded-full px-3 py-1 font-mono-lbl text-[10px] uppercase tracking-widest ${j.leg === "OUT" ? "bg-[#38bdf8]/15 text-[#38bdf8]" : "bg-emerald-400/15 text-emerald-400"}`}>
                      {j.leg === "OUT" ? <Plane className="h-3 w-3" /> : <PlaneLanding className="h-3 w-3" />} {j.leg}
                    </span>
                    <div>
                      <div className={`font-disp text-lg text-white ${j.jobStatus === "completed" ? "line-through" : ""}`}>{j.time} · {j.route}</div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-3 font-mono-lbl text-[11px] text-slate-400">
                        <span>{j.b.name}</span>
                        <span className="flex items-center gap-1"><Users className="h-3 w-3" />{j.b.passengers}</span>
                        <span className="flex items-center gap-1"><Car className="h-3 w-3" />{j.b.vehicle_name}</span>
                        {j.flight && <span className="text-[#38bdf8]">{j.flight} tracked</span>}
                        <span data-testid={`job-status-${j.key}`} className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest ${
                          j.jobStatus === "completed" ? "bg-emerald-400/15 text-emerald-400"
                          : j.jobStatus === "assigned" ? "bg-[#38bdf8]/15 text-[#38bdf8]"
                          : "bg-amber-400/15 text-amber-300"}`}>{j.jobStatus}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 self-start sm:self-center">
                    {j.jobStatus === "pending" && (
                      <button onClick={() => setLeg(j.b.id, j.legKey, "assigned")} data-testid={`job-assign-${j.key}`}
                        className="flex items-center gap-1.5 rounded-full bg-[#38bdf8] px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-wider text-[#0b101b] transition-colors hover:bg-white">
                        <UserCheck className="h-3 w-3" /> Assign driver
                      </button>
                    )}
                    {j.jobStatus === "assigned" && (
                      <button onClick={() => setLeg(j.b.id, j.legKey, "completed")} data-testid={`job-complete-${j.key}`}
                        className="flex items-center gap-1.5 rounded-full bg-emerald-400 px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-wider text-[#0b101b] transition-colors hover:bg-white">
                        <CheckCircle2 className="h-3 w-3" /> Complete
                      </button>
                    )}
                    {j.jobStatus !== "pending" && (
                      <button onClick={() => setLeg(j.b.id, j.legKey, "pending")} data-testid={`job-reset-${j.key}`}
                        className="rounded-full border border-white/15 px-3 py-1.5 font-mono-lbl text-[10px] uppercase tracking-wider text-slate-400 transition-colors hover:border-white/40 hover:text-white">
                        Reopen
                      </button>
                    )}
                    <a href={`tel:${j.b.phone}`} data-testid={`schedule-call-${j.key}`}
                      className="flex items-center gap-2 rounded-full border border-white/15 px-3.5 py-1.5 font-mono-lbl text-xs text-slate-300 transition-colors hover:border-[#38bdf8]/50 hover:text-[#38bdf8]">
                      <Phone className="h-3 w-3" /> {j.b.phone}
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* All bookings */}
        <section className="mt-10" data-testid="bookings-section">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
            <h2 className="font-disp text-2xl uppercase text-white">All Bookings</h2>
            <div className="flex rounded-full border border-white/15 bg-[#0b101b] p-1" data-testid="bookings-filter-tabs">
              {[["paid", "Paid"], ["awaiting", "Awaiting"], ["all", "All"]].map(([v, l]) => (
                <button key={v} data-testid={`tab-${v}`} onClick={() => setTab(v)}
                  className={`rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${tab === v ? "bg-[#38bdf8] text-[#0b101b]" : "text-slate-400 hover:text-white"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div className="overflow-x-auto rounded-2xl border border-white/10" data-testid="bookings-table">
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead>
                <tr className="border-b border-white/10 bg-white/5 font-mono-lbl text-[10px] uppercase tracking-widest text-slate-400">
                  <th className="px-4 py-3">Ref</th><th className="px-4 py-3">Journey</th><th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">Vehicle</th><th className="px-4 py-3">Fare</th><th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((b) => (
                  <tr key={b.id} data-testid={`booking-row-${b.reference}`} className="border-b border-white/5 last:border-0 hover:bg-white/5">
                    <td className="px-4 py-3 font-mono-lbl text-xs text-[#38bdf8]">{b.reference}</td>
                    <td className="px-4 py-3">
                      <div className="text-white">{b.direction === "to_airport" ? `${b.pickup_zone} → ${b.airport_name}` : `${b.airport_name} → ${b.pickup_zone}`}</div>
                      <div className="mt-0.5 font-mono-lbl text-[11px] text-slate-400">
                        {b.date} {b.time}{b.return_journey && ` · rtn ${b.return_date} ${b.return_time}`}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-white">{b.name}</div>
                      <div className="mt-0.5 font-mono-lbl text-[11px] text-slate-400">{b.phone}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-300">{b.vehicle_name} · {b.passengers} pax</td>
                    <td className="px-4 py-3 font-bold text-white">{formatGBP(b.amount_gbp)}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2.5 py-1 font-mono-lbl text-[10px] uppercase tracking-widest ${b.status === "paid" ? "bg-emerald-400/15 text-emerald-400" : "bg-amber-400/15 text-amber-300"}`}
                        data-testid={`booking-status-${b.reference}`}>
                        {b.status === "paid" ? "Paid" : "Awaiting"}
                      </span>
                    </td>
                  </tr>
                ))}
                {shown.length === 0 && (
                  <tr><td colSpan="6" className="px-4 py-8 text-center text-sm text-slate-500" data-testid="bookings-empty">No bookings in this view.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}
